import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API_BASE_URL from "./config";
import "./VideoPlayer.css";

function VideoPlayer() {
  const { token } = useParams();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedQuality, setSelectedQuality] = useState(null);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchVideo();
  }, [token]);

  const fetchVideo = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/videos/shared/${token}`);
      if (!res.ok) throw new Error("Video not found");
      const data = await res.json();
      setVideo(data);
      // Default to best available quality
      if (data.qualities && data.qualities.length > 0) {
        setSelectedQuality(data.qualities[data.qualities.length - 1]);
      }
    } catch (err) {
      setError("Video not found or link has expired");
    } finally {
      setLoading(false);
    }
  };

  const changeQuality = (q) => {
    const currentTime = videoRef.current?.currentTime || 0;
    const wasPlaying = videoRef.current && !videoRef.current.paused;
    setSelectedQuality(q);
    setShowQualityMenu(false);
    // Restore position after source change
    setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = currentTime;
        if (wasPlaying) videoRef.current.play().catch(() => {});
      }
    }, 100);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current.requestFullscreen().catch(() => {});
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => document.removeEventListener("fullscreenchange", handler);
  }, []);

  const formatDuration = (s) => {
    if (!s) return "";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="vp-loading">
        <div className="vp-spinner" />
        <p>Loading video...</p>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="vp-error">
        <h2>😔 {error || "Video not found"}</h2>
        <p>This video may have been removed or the link is invalid.</p>
      </div>
    );
  }

  return (
    <div className="vp-page" ref={containerRef}>
      <div className="vp-header">
        <div className="vp-header-left">
          <span className="vp-logo">🎬 Photentra</span>
        </div>
        <div className="vp-header-right">
          <button className="vp-copy-btn" onClick={copyLink}>
            {copied ? "✅ Copied!" : "🔗 Copy Link"}
          </button>
        </div>
      </div>

      <div className="vp-player-wrapper">
        {selectedQuality ? (
          <video
            ref={videoRef}
            key={selectedQuality.label}
            className="vp-video"
            controls
            autoPlay
            poster={video.thumbnail_url}
            src={selectedQuality.url}
          />
        ) : (
          <div className="vp-no-video">Video is not yet available</div>
        )}

        {/* Quality selector */}
        {video.qualities && video.qualities.length > 1 && (
          <div className="vp-quality-wrapper">
            <button
              className="vp-quality-btn"
              onClick={() => setShowQualityMenu(!showQualityMenu)}
            >
              ⚙️ {selectedQuality?.label || "Quality"}
            </button>
            {showQualityMenu && (
              <div className="vp-quality-menu">
                {video.qualities.map((q) => (
                  <button
                    key={q.label}
                    className={`vp-quality-option ${selectedQuality?.label === q.label ? "active" : ""}`}
                    onClick={() => changeQuality(q)}
                  >
                    {q.label}
                    {selectedQuality?.label === q.label && " ✓"}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="vp-meta">
        <h1 className="vp-title">{video.original_filename}</h1>
        <div className="vp-meta-row">
          {video.event_name && <span className="vp-event-tag">📁 {video.event_name}</span>}
          {video.duration && <span className="vp-duration">⏱ {formatDuration(video.duration)}</span>}
          <span className="vp-date">
            📅 {new Date(video.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>
    </div>
  );
}

export default VideoPlayer;
